/**
 * SEO Asset Creation Component
 * Handles On-Page SEO, Technical SEO, Schema Markup
 */

import React, { useState } from 'react';
import { Globe, Search, Code, Upload, Eye, Send, ChevronLeft, ChevronRight, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface SEOAssetProps {
  clientId?: number;
  planId?: number;
  projectId?: number;
  onComplete?: (contentId: number) => void;
  onCancel?: () => void;
}

interface SEOType {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const seoTypes: SEOType[] = [
  { id: 'onpage_seo', name: 'On-Page SEO', description: 'Meta tags, content optimization', icon: <Search className="w-5 h-5" />, color: 'bg-blue-500' },
  { id: 'technical_seo', name: 'Technical SEO', description: 'Site structure, performance', icon: <Code className="w-5 h-5" />, color: 'bg-purple-500' },
  { id: 'schema_markup', name: 'Schema Markup', description: 'Structured data, rich snippets', icon: <Globe className="w-5 h-5" />, color: 'bg-green-500' },
];

export default function SEOAssetCreation({ clientId, planId, projectId, onComplete, onCancel }: SEOAssetProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSEOType, setSelectedSEOType] = useState<SEOType | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const [onPageData, setOnPageData] = useState({
    pageUrl: '',
    pageTitle: '',
    metaDescription: '',
    h1Tag: '',
    targetKeyword: '',
    secondaryKeywords: '',
    focusKeyphrase: '',
    canonicalUrl: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterCard: 'summary_large_image',
    twitterTitle: '',
    twitterDescription: '',
  });

  const [technicalData, setTechnicalData] = useState({
    auditType: '',
    issueDescription: '',
    recommendations: '',
    priority: 'medium',
    robotsTxt: '',
    sitemapXml: '',
    htaccess: '',
    redirects: '',
  });

  const [schemaData, setSchemaData] = useState({
    schemaType: '',
    schemaJson: '',
  });

  const handleFilesUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const validateSEO = () => {
    const issues = [];
    const warnings = [];
    const passed = [];

    // Page Title
    if (!onPageData.pageTitle) {
      issues.push('Page title is required');
    } else if (onPageData.pageTitle.length < 30) {
      warnings.push('Page title too short (< 30 chars)');
    } else if (onPageData.pageTitle.length > 60) {
      issues.push('Page title too long (> 60 chars)');
    } else {
      passed.push('Page title length is optimal');
    }

    // Meta Description
    if (!onPageData.metaDescription) {
      issues.push('Meta description is required');
    } else if (onPageData.metaDescription.length < 120) {
      warnings.push('Meta description too short (< 120 chars)');
    } else if (onPageData.metaDescription.length > 160) {
      issues.push('Meta description too long (> 160 chars)');
    } else {
      passed.push('Meta description length is optimal');
    }

    // Target Keyword
    if (!onPageData.targetKeyword) {
      issues.push('Target keyword is required');
    } else {
      passed.push('Target keyword defined');
    }

    // H1 Tag
    if (!onPageData.h1Tag) {
      warnings.push('H1 tag is recommended');
    } else {
      passed.push('H1 tag defined');
    }

    // Canonical URL
    if (!onPageData.canonicalUrl) {
      warnings.push('Canonical URL is recommended');
    } else {
      passed.push('Canonical URL set');
    }

    return { issues, warnings, passed };
  };

  const validateSchema = () => {
    if (!schemaData.schemaJson) return { valid: false, error: 'Schema JSON is required' };

    try {
      const parsed = JSON.parse(schemaData.schemaJson);
      if (!parsed['@context']) return { valid: false, error: 'Missing @context' };
      if (!parsed['@type']) return { valid: false, error: 'Missing @type' };
      return { valid: true, error: null };
    } catch (e) {
      return { valid: false, error: 'Invalid JSON format' };
    }
  };

  const handleSubmit = async () => {
    if (!selectedSEOType) return;

    setLoading(true);

    try {
      let configData: any = {
        seo_type: selectedSEOType.id,
      };

      if (selectedSEOType.id === 'onpage_seo') {
        configData = { ...configData, on_page: onPageData, seo_audit: validateSEO() };
      } else if (selectedSEOType.id === 'technical_seo') {
        configData = { ...configData, technical: technicalData };
      } else if (selectedSEOType.id === 'schema_markup') {
        configData = { ...configData, schema: schemaData, validation: validateSchema() };
      }

      const contentPayload = {
        type: 'seo',
        content_subtype: selectedSEOType.id,
        title: selectedSEOType.id === 'onpage_seo' ? onPageData.pageTitle :
               selectedSEOType.id === 'technical_seo' ? technicalData.auditType :
               `${schemaData.schemaType} Schema`,
        description: selectedSEOType.id === 'onpage_seo' ? onPageData.metaDescription :
                     technicalData.issueDescription,
        status: 'draft',
        client_id: clientId,
        plan_id: planId,
        project_id: projectId,
        platform_config: JSON.stringify(configData),
      };

      const contentRes = await axios.post(`${API_BASE_URL}/content`, contentPayload);
      const contentId = contentRes.data.id;

      // Upload files
      if (files.length > 0) {
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('content_id', contentId.toString());
          formData.append('asset_type', 'document');
          formData.append('asset_group', 'supplementary');

          await axios.post(`${API_BASE_URL}/assets/upload`, formData);
        }
      }

      // Create workflow steps
      const workflowSteps = [
        { step_order: 1, step_name: 'SEO Type Selection', step_type: 'metadata', status: 'completed' },
        { step_order: 2, step_name: 'SEO Configuration', step_type: 'content', status: 'completed' },
        { step_order: 3, step_name: 'Files Upload', step_type: 'assets', status: 'completed' },
        { step_order: 4, step_name: 'SEO Audit', step_type: 'preview', status: 'completed' },
        { step_order: 5, step_name: 'Submit for Approval', step_type: 'approval', status: 'pending' },
      ];

      for (const step of workflowSteps) {
        await axios.post(`${API_BASE_URL}/content/${contentId}/workflow/steps`, step);
      }

      alert('SEO content created successfully!');
      if (onComplete) onComplete(contentId);
    } catch (error) {
      console.error('Failed to create SEO asset:', error);
      alert('Failed to create SEO content');
    } finally {
      setLoading(false);
    }
  };

  const seoAudit = selectedSEOType?.id === 'onpage_seo' ? validateSEO() : null;
  const schemaValidation = selectedSEOType?.id === 'schema_markup' ? validateSchema() : null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create SEO Content</h1>
              <p className="text-gray-600 mt-1">On-page optimization, technical SEO, and schema markup</p>
            </div>
            {onCancel && (
              <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            )}
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-6">
            {[
              { num: 1, label: 'Type' },
              { num: 2, label: 'Configure' },
              { num: 3, label: 'Files' },
              { num: 4, label: 'Audit' },
            ].map((step, i) => (
              <React.Fragment key={step.num}>
                <div className={`flex items-center ${currentStep >= step.num ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step.num ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>
                    {step.num}
                  </div>
                  <span className="ml-2 text-sm font-medium hidden sm:inline">{step.label}</span>
                </div>
                {i < 3 && <ChevronRight className="w-4 h-4 text-gray-400 mx-2" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Step 1: SEO Type Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Select SEO Type</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {seoTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedSEOType(type)}
                    className={`p-6 rounded-lg border-2 transition text-left ${
                      selectedSEOType?.id === type.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-12 h-12 ${type.color} rounded-lg flex items-center justify-center mb-3 text-white`}>
                      {type.icon}
                    </div>
                    <h3 className="font-semibold text-gray-900">{type.name}</h3>
                    <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                  </button>
                ))}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!selectedSEOType}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Next: Configuration
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Configuration */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">
                {selectedSEOType?.name} Configuration
              </h2>

              {/* On-Page SEO Form */}
              {selectedSEOType?.id === 'onpage_seo' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Page URL *</label>
                    <input
                      type="url"
                      value={onPageData.pageUrl}
                      onChange={(e) => setOnPageData({ ...onPageData, pageUrl: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="https://example.com/page"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Page Title (SEO) *</label>
                    <input
                      type="text"
                      value={onPageData.pageTitle}
                      onChange={(e) => setOnPageData({ ...onPageData, pageTitle: e.target.value })}
                      maxLength={60}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">{onPageData.pageTitle.length}/60 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description *</label>
                    <textarea
                      value={onPageData.metaDescription}
                      onChange={(e) => setOnPageData({ ...onPageData, metaDescription: e.target.value })}
                      maxLength={160}
                      rows={3}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">{onPageData.metaDescription.length}/160 characters</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">H1 Tag</label>
                      <input
                        type="text"
                        value={onPageData.h1Tag}
                        onChange={(e) => setOnPageData({ ...onPageData, h1Tag: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Canonical URL</label>
                      <input
                        type="url"
                        value={onPageData.canonicalUrl}
                        onChange={(e) => setOnPageData({ ...onPageData, canonicalUrl: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target Keyword *</label>
                      <input
                        type="text"
                        value={onPageData.targetKeyword}
                        onChange={(e) => setOnPageData({ ...onPageData, targetKeyword: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Keywords</label>
                      <input
                        type="text"
                        value={onPageData.secondaryKeywords}
                        onChange={(e) => setOnPageData({ ...onPageData, secondaryKeywords: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="keyword1, keyword2, keyword3"
                      />
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-blue-900 mb-3">Open Graph Tags</h3>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={onPageData.ogTitle}
                        onChange={(e) => setOnPageData({ ...onPageData, ogTitle: e.target.value })}
                        className="w-full px-4 py-2 border border-blue-200 rounded-lg"
                        placeholder="OG Title"
                      />
                      <textarea
                        value={onPageData.ogDescription}
                        onChange={(e) => setOnPageData({ ...onPageData, ogDescription: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-2 border border-blue-200 rounded-lg"
                        placeholder="OG Description"
                      />
                      <input
                        type="url"
                        value={onPageData.ogImage}
                        onChange={(e) => setOnPageData({ ...onPageData, ogImage: e.target.value })}
                        className="w-full px-4 py-2 border border-blue-200 rounded-lg"
                        placeholder="OG Image URL"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Technical SEO Form */}
              {selectedSEOType?.id === 'technical_seo' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Audit Type *</label>
                    <select
                      value={technicalData.auditType}
                      onChange={(e) => setTechnicalData({ ...technicalData, auditType: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select type...</option>
                      <option value="site_structure">Site Structure</option>
                      <option value="performance">Performance Optimization</option>
                      <option value="crawlability">Crawlability & Indexing</option>
                      <option value="redirects">Redirects & URLs</option>
                      <option value="mobile">Mobile Optimization</option>
                      <option value="security">Security (HTTPS)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issue Description</label>
                    <textarea
                      value={technicalData.issueDescription}
                      onChange={(e) => setTechnicalData({ ...technicalData, issueDescription: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Describe the technical SEO issue..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Recommendations</label>
                    <textarea
                      value={technicalData.recommendations}
                      onChange={(e) => setTechnicalData({ ...technicalData, recommendations: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Implementation recommendations..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={technicalData.priority}
                      onChange={(e) => setTechnicalData({ ...technicalData, priority: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-sm font-semibold text-purple-900 mb-3">Configuration Files (Optional)</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-purple-700">robots.txt</label>
                        <textarea
                          value={technicalData.robotsTxt}
                          onChange={(e) => setTechnicalData({ ...technicalData, robotsTxt: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-purple-200 rounded font-mono text-xs"
                          placeholder="User-agent: *&#10;Disallow: /admin/"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-purple-700">Redirects</label>
                        <textarea
                          value={technicalData.redirects}
                          onChange={(e) => setTechnicalData({ ...technicalData, redirects: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-purple-200 rounded font-mono text-xs"
                          placeholder="/old-page /new-page 301"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Schema Markup Form */}
              {selectedSEOType?.id === 'schema_markup' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Schema Type *</label>
                    <select
                      value={schemaData.schemaType}
                      onChange={(e) => setSchemaData({ ...schemaData, schemaType: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select schema type...</option>
                      <option value="Article">Article</option>
                      <option value="LocalBusiness">Local Business</option>
                      <option value="Product">Product</option>
                      <option value="Organization">Organization</option>
                      <option value="Person">Person</option>
                      <option value="Event">Event</option>
                      <option value="Recipe">Recipe</option>
                      <option value="FAQPage">FAQ Page</option>
                      <option value="BreadcrumbList">Breadcrumb</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Schema JSON-LD *</label>
                    <textarea
                      value={schemaData.schemaJson}
                      onChange={(e) => setSchemaData({ ...schemaData, schemaJson: e.target.value })}
                      rows={15}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder={`{
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Article Title",
  "author": {
    "@type": "Person",
    "name": "Author Name"
  },
  "datePublished": "2024-01-01"
}`}
                    />
                  </div>

                  {schemaValidation && !schemaValidation.valid && schemaData.schemaJson && (
                    <div className="bg-red-50 p-4 rounded-lg flex items-start gap-3">
                      <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-900">Schema Validation Error</p>
                        <p className="text-sm text-red-700 mt-1">{schemaValidation.error}</p>
                      </div>
                    </div>
                  )}

                  {schemaValidation && schemaValidation.valid && (
                    <div className="bg-green-50 p-4 rounded-lg flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-green-900">Schema Valid</p>
                        <p className="text-sm text-green-700 mt-1">JSON-LD structure is correct</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Next: Files
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Files Upload */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Upload Files (Optional)</h2>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <label htmlFor="files-upload" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-700 font-medium">Click to upload</span>
                    <span className="text-gray-500"> or drag and drop</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">Sitemap, robots.txt, schema files, etc.</p>
                  <input
                    id="files-upload"
                    type="file"
                    onChange={handleFilesUpload}
                    multiple
                    className="hidden"
                  />
                </div>

                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(0)} KB</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Eye className="w-4 h-4 inline mr-1" />
                  SEO Audit
                </button>
              </div>
            </div>
          )}

          {/* Step 4: SEO Audit & Submit */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">SEO Audit & Submit</h2>

              {/* On-Page SEO Audit */}
              {selectedSEOType?.id === 'onpage_seo' && seoAudit && (
                <div className="space-y-4">
                  {seoAudit.issues.length > 0 && (
                    <div className="bg-red-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <XCircle className="w-5 h-5 text-red-600" />
                        <h3 className="font-semibold text-red-900">Issues ({seoAudit.issues.length})</h3>
                      </div>
                      <ul className="text-sm text-red-700 space-y-1">
                        {seoAudit.issues.map((issue, i) => (
                          <li key={i}>• {issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {seoAudit.warnings.length > 0 && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600" />
                        <h3 className="font-semibold text-yellow-900">Warnings ({seoAudit.warnings.length})</h3>
                      </div>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        {seoAudit.warnings.map((warning, i) => (
                          <li key={i}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {seoAudit.passed.length > 0 && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <h3 className="font-semibold text-green-900">Passed ({seoAudit.passed.length})</h3>
                      </div>
                      <ul className="text-sm text-green-700 space-y-1">
                        {seoAudit.passed.map((pass, i) => (
                          <li key={i}>• {pass}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Google Preview */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Google Search Preview</h3>
                    <div>
                      <div className="text-blue-600 text-sm">{onPageData.pageTitle || 'Page Title Here'}</div>
                      <div className="text-green-700 text-xs">{onPageData.canonicalUrl || onPageData.pageUrl || 'https://example.com/page'}</div>
                      <div className="text-gray-600 text-sm mt-1">{onPageData.metaDescription || 'Meta description will appear here...'}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Technical SEO Summary */}
              {selectedSEOType?.id === 'technical_seo' && (
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-600">Audit Type:</span>
                      <span className="ml-2 font-medium">{technicalData.auditType}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Priority:</span>
                      <span className={`ml-2 px-2 py-1 rounded text-sm ${
                        technicalData.priority === 'critical' ? 'bg-red-100 text-red-700' :
                        technicalData.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                        technicalData.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {technicalData.priority.charAt(0).toUpperCase() + technicalData.priority.slice(1)}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Files Attached:</span>
                      <span className="ml-2 font-medium">{files.length}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Schema Markup Summary */}
              {selectedSEOType?.id === 'schema_markup' && (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="space-y-3">
                      <div>
                        <span className="text-sm text-gray-600">Schema Type:</span>
                        <span className="ml-2 font-medium">{schemaData.schemaType}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Validation:</span>
                        <span className={`ml-2 px-2 py-1 rounded text-sm ${
                          schemaValidation?.valid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {schemaValidation?.valid ? 'Valid' : 'Invalid'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {schemaValidation?.valid && (
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <p className="text-sm text-blue-900">
                        ✓ Schema markup is ready for implementation. Test with Google's Rich Results Test after deployment.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Approval
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
